import React, { useState, useEffect, useCallback, useMemo, Suspense, lazy } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { irAlPanel } from '../lib/paneles';
import { api } from '../lib/api';
import ComercioNombre from '../components/ComercioNombre';
import { distanciaKm } from '../lib/distancia';

// El mapa (Leaflet) se carga solo cuando hace falta
const Mapa = lazy(() => import('../components/Mapa'));
import { fmtARS } from '../lib/money';
import Navbar from '../components/Navbar';
import { ToastContainer } from '../components/Toast';
import { useToast } from '../hooks/useToast';
import '../styles/Regalos.css';
import { DELIVERY, DELIVERY_SRCSET } from '../lib/imagenes';

const STATUS_LABELS = {
  pending_payment: 'Pago pendiente',
  paid: 'Esperando respuesta',
  accepted: 'Aceptado · en preparación',
  delivered: 'Entregado',
  rejected: 'Rechazado · dinero devuelto',
  refunded: 'Dinero devuelto',
  expired: 'Vencido',
};
const STATUS_BADGE = {
  pending_payment: 'badge-gris',
  paid: 'badge-dorado',
  accepted: 'badge-verde',
  delivered: 'badge-verde',
  rejected: 'badge-rojo',
  refunded: 'badge-gris',
  expired: 'badge-gris',
};
const PAGO_MSGS = {
  ok: ['success', '¡Pago aprobado! Le avisamos a tu match.'],
  error: ['error', 'El pago no se completó.'],
  pendiente: ['info', 'Tu pago está pendiente de acreditación.'],
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('es-AR') : '';

function Avatar({ foto, name }) {
  return (
    <div className="regalo-avatar">
      {foto ? <img src={foto} alt={name} /> : <span>{(name || 'U')[0].toUpperCase()}</span>}
    </div>
  );
}

function StatusBadge({ status }) {
  return <span className={`badge ${STATUS_BADGE[status] || 'badge-gris'}`}>{STATUS_LABELS[status] || status}</span>;
}

function AceptarForm({ onSubmit, onCancel, busy, zona, comercio }) {
  const [direccion, setDireccion] = useState('');
  const [punto, setPunto] = useState(null);
  const elegir = useCallback((lat, lng) => setPunto({ lat, lng }), []);
  const circulo = useMemo(() => zona && { ...zona }, [zona]);
  const fuera = zona && punto && distanciaKm(zona, punto) > zona.radio_km;
  const [referencias, setReferencias] = useState('');
  const [horario, setHorario] = useState('');
  const [err, setErr] = useState('');

  const submit = (e) => {
    e.preventDefault();
    const d = direccion.trim();
    if (d.length < 5 || d.length > 200) { setErr('La dirección debe tener entre 5 y 200 caracteres.'); return; }
    if (zona && !punto) { setErr('Marcá en el mapa dónde querés recibirlo.'); return; }
    if (fuera) { setErr('Ese punto queda fuera de la zona de entrega.'); return; }
    setErr('');
    onSubmit({ direccion: d, referencias: referencias.trim(), horario: horario.trim(), ...(punto || {}) });
  };

  return (
    <form className="regalo-form" onSubmit={submit}>
      <div className="form-group">
        <label className="form-label">Dirección de entrega</label>
        <input className={`form-input ${err ? 'input-error' : ''}`} value={direccion} maxLength={200}
          onChange={e => setDireccion(e.target.value)} placeholder="Calle, número, localidad" />
        {err && <span className="form-error">{err}</span>}
      </div>
      <div className="form-group">
        <label className="form-label">Referencias (opcional)</label>
        <input className="form-input" value={referencias} maxLength={200}
          onChange={e => setReferencias(e.target.value)} placeholder="timbre 3B" />
      </div>
      <div className="form-group">
        <label className="form-label">Horario (opcional)</label>
        <input className="form-input" value={horario} maxLength={100}
          onChange={e => setHorario(e.target.value)} placeholder="de 18 a 21 hs" />
      </div>
      {zona && (
        <div className="form-group">
          <label className="form-label">¿Dónde lo recibís?</label>
          <p className="regalo-note" style={{ marginBottom: 8 }}>
            Tocá el mapa en el lugar de entrega. {comercio} entrega hasta {zona.radio_km} km de su local: tiene que quedar dentro del círculo.
          </p>
          <Suspense fallback={<p className="regalo-note">Cargando mapa…</p>}><Mapa circulo={circulo} marcador={punto} onElegir={elegir} alto={280} /></Suspense>
          {fuera && <span className="form-error">Ese punto queda fuera de la zona de entrega de {comercio}. Elegí otro dentro del círculo, o rechazá el regalo y se le devuelve el dinero a quien te lo mandó.</span>}
        </div>
      )}
      <p className="regalo-note">Tu dirección solo la ve el comercio para hacer la entrega. El envío lo hace el comercio por su cuenta y es su responsabilidad que llegue; Cupido con Mate no se hace responsable de los envíos.</p>
      <div className="regalo-actions">
        <button type="button" className="btn btn-outline btn-sm" onClick={onCancel}>Cancelar</button>
        <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>
          {busy ? 'Enviando...' : 'Confirmar y aceptar'}
        </button>
      </div>
    </form>
  );
}

// Comercios aprobados de Cupido Delivery, con el precio final (producto + envío + 10% de la app)
function ComerciosAdheridos() {
  const [comercios, setComercios] = useState(null);
  useEffect(() => {
    api.regalosCatalogo().then(d => setComercios(d.comercios || [])).catch(() => setComercios([]));
  }, []);
  if (!comercios || comercios.length === 0) return null;
  return (
    <>
      <h2 className="regalos-section-title">🏪 Comercios de Cupido Delivery</h2>
      <p className="regalo-note" style={{ marginBottom: 12 }}>Para regalar, abrí el chat con tu conexión y tocá «🎁 Regalar» (Plan Mate o Premium). Los precios ya incluyen el envío. Se muestran los comercios abiertos en este momento.</p>
      {comercios.map(c => (
        <div key={c.id} className="regalar-comercio">
          <ComercioNombre comercio={c} />
          {c.descripcion && <div className="regalar-producto-desc" style={{ marginBottom: 4 }}>{c.descripcion}</div>}
          <div className="comercio-zona-info">
                {c.abierto_hasta && <>🟢 Abierto hasta las {c.abierto_hasta}</>}
                {c.abierto_hasta && c.zona && ' · '}
                {c.zona && <>📍 Entrega hasta {c.zona.radio_km} km de su local</>}
              </div>
          <div className="regalar-productos">
            {(c.productos || []).map(p => (
              <div key={p.id} className="regalar-producto" style={{ cursor: 'default' }}>
                {p.foto_path && <img src={p.foto_path} alt={p.nombre} />}
                <span className="regalar-producto-nombre">{p.nombre}</span>
                {p.descripcion && <span className="regalar-producto-desc">{p.descripcion}</span>}
                <span className="regalar-producto-precio">{fmtARS(p.precio)}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
      <p className="regalo-note" style={{ marginTop: 8 }}>
        ⚠️ Cupido con Mate no se hace responsable de los envíos: cada comercio hace la entrega por su cuenta y es responsable de que el regalo llegue.
      </p>
    </>
  );
}

export default function RegalosPage() {
  const { toasts, success, error, info } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState({ recibidos: [], enviados: [] });
  const [loading, setLoading] = useState(true);
  const [aceptando, setAceptando] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await api.misRegalos();
      setData({ recibidos: d.recibidos || [], enviados: d.enviados || [] });
    } catch (e) {
      error(e.message || 'Error cargando regalos.');
    } finally {
      setLoading(false);
    }
  }, [error]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const pago = searchParams.get('pago');
    if (!pago || !PAGO_MSGS[pago]) return;
    const [type, msg] = PAGO_MSGS[pago];
    ({ success, error, info })[type](msg);
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams, success, error, info]);

  const aceptar = async (id, form) => {
    setBusy(true);
    try {
      await api.aceptarRegalo(id, form);
      success('¡Regalo aceptado! El comercio lo va a preparar.');
      setAceptando(null);
      load();
    } catch (e) { error(e.message || 'No se pudo aceptar el regalo.'); }
    finally { setBusy(false); }
  };

  const rechazar = async (id) => {
    if (!window.confirm('¿Rechazar este regalo? Se le devuelve el dinero a quien te lo mandó.')) return;
    try {
      await api.rechazarRegalo(id);
      success('Regalo rechazado.');
      load();
    } catch (e) { error(e.message || 'No se pudo rechazar el regalo.'); }
  };

  return (
    <div className="regalos-page">
      <Navbar />
      <ToastContainer toasts={toasts} />
      <div className="regalos-container container">
        <div className="regalos-header">
          <img src={DELIVERY} srcSet={DELIVERY_SRCSET} sizes="(max-width: 600px) 90px, 120px" alt="Cupido Delivery" className="cupido-delivery-logo" />
          <div>
            <h1 className="regalos-title">🎁 Regalos y +</h1>
            <p className="regalos-sub">Con Cupido Delivery le mandás regalos de comercios de San Juan a tus matches.</p>
          </div>
        </div>

        {loading ? (
          <p className="regalos-empty">Cargando...</p>
        ) : (
          <>
            <h2 className="regalos-section-title">Recibidos</h2>
            {data.recibidos.length === 0 ? (
              <p className="regalos-empty">Todavía no recibiste regalos.</p>
            ) : (
              <div className="regalos-list">
                {data.recibidos.map(r => (
                  <div key={r.id} className="regalo-card">
                    <Avatar foto={r.from_foto} name={r.from_name} />
                    <div className="regalo-body">
                      <div className="regalo-head">
                        <span className="regalo-name">{r.from_name}</span>
                        <StatusBadge status={r.status} />
                      </div>
                      <div className="regalo-detail">🎁 {r.producto_nombre} · 🏪 {r.comercio_nombre}</div>
                      {r.mensaje && <div className="regalo-msg">“{r.mensaje}”</div>}
                      <div className="regalo-detail" style={{ fontSize: 12 }}>{fmtDate(r.created_at)}</div>
                      {r.status === 'paid' && aceptando !== r.id && (
                        <div className="regalo-actions">
                          <button className="btn btn-primary btn-sm" onClick={() => setAceptando(r.id)}>Aceptar</button>
                          <button className="btn btn-outline btn-sm" onClick={() => rechazar(r.id)}>Rechazar</button>
                        </div>
                      )}
                      {r.status === 'paid' && aceptando === r.id && (
                        <AceptarForm busy={busy} zona={r.zona} comercio={r.comercio_nombre} onCancel={() => setAceptando(null)} onSubmit={(f) => aceptar(r.id, f)} />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <h2 className="regalos-section-title">Enviados</h2>
            {data.enviados.length === 0 ? (
              <p className="regalos-empty">Todavía no mandaste regalos. Probá desde el chat con un match.</p>
            ) : (
              <div className="regalos-list">
                {data.enviados.map(r => (
                  <div key={r.id} className="regalo-card">
                    <Avatar foto={r.to_foto} name={r.to_name} />
                    <div className="regalo-body">
                      <div className="regalo-head">
                        <span className="regalo-name">Para {r.to_name}</span>
                        <StatusBadge status={r.status} />
                      </div>
                      <div className="regalo-detail">🎁 {r.producto_nombre} · 🏪 {r.comercio_nombre} · {fmtARS(r.precio)}</div>
                      <div className="regalo-detail" style={{ fontSize: 12 }}>{fmtDate(r.created_at)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <ComerciosAdheridos />
            <p className="regalo-note" style={{ marginTop: 24, textAlign: 'center' }}>
              ¿Tenés un comercio en San Juan? <Link to="/comercio" onClick={(e) => { e.preventDefault(); irAlPanel('/comercio', navigate); }} style={{ color: 'var(--dorado-claro)' }}>Sumalo a Cupido Delivery</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
